import { useState, useCallback } from 'react';
import Konva from 'konva';
import { useVisibleSortedElements } from './useTemplateStore';
import { Camera } from '../utils/cameraUtils';

/**
 * Configuration for snap-to-grid functionality
 */
export interface SnapConfig {
  /** Enable/disable snapping to grid */
  enabled: boolean;
  /** Snap threshold in pixels */
  threshold: number;
  /** Show snap lines */
  showLines: boolean;
}

/**
 * Snap line data structure
 */
export interface SnapLine {
  points: number[];
}

/**
 * Type for a snapping guide line.
 */
export interface Guide {
  guide: number;
  offset: number;
  snap: 'start' | 'center' | 'end';
}

/**
 * Hook for handling snapping functionality in the canvas
 */
export const useSnapping = (camera: Camera, templateWidth: number, templateHeight: number) => {
  const elements = useVisibleSortedElements();
  const [snapLines, setSnapLines] = useState<SnapLine[]>([]);
  const [stage, setStage] = useState<Konva.Stage | null>(null);

  // Snap configuration
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const snapConfig: SnapConfig = {
    enabled: true,
    threshold: 8, // Screen pixels
    showLines: true
  };

  // Separate threshold for resize operations (less sensitive)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resizeSnapConfig: SnapConfig = {
    enabled: true,
    threshold: 6, // Screen pixels
    showLines: true
  };

  /**
   * Update stage reference
   */
  const updateStage = useCallback((newStage: Konva.Stage | null) => {
    setStage(newStage);
  }, []);

  /**
   * Universal function to get snapping edges for any node.
   */
  const getNodeSnappingEdges = useCallback((node: Konva.Node) => {
    const box = node.getClientRect({ relativeTo: node.getParent() || undefined });

    return {
      vertical: [
        { guide: box.x, snap: 'start' as const },
        { guide: box.x + box.width / 2, snap: 'center' as const },
        { guide: box.x + box.width, snap: 'end' as const },
      ],
      horizontal: [
        { guide: box.y, snap: 'start' as const },
        { guide: box.y + box.height / 2, snap: 'center' as const },
        { guide: box.y + box.height, snap: 'end' as const },
      ],
    };
  }, []);

  /**
   * Get all possible guides for snapping
   */
  const getGuides = useCallback((excludeNodeId?: string) => {
    if (!stage) return { vertical: [], horizontal: [] };

    const vertical: Guide[] = [];
    const horizontal: Guide[] = [];

    // Add template boundaries (as world coordinates)
    vertical.push({ guide: 0, offset: 0, snap: 'start' });
    vertical.push({ guide: templateWidth / 2, offset: 0, snap: 'center' });
    vertical.push({ guide: templateWidth, offset: 0, snap: 'end' });
    horizontal.push({ guide: 0, offset: 0, snap: 'start' });
    horizontal.push({ guide: templateHeight / 2, offset: 0, snap: 'center' });
    horizontal.push({ guide: templateHeight, offset: 0, snap: 'end' });

    // Add element edges (exclude the node being dragged)
    elements.forEach(element => {
      if (element.id === excludeNodeId) return;

      const node = stage.findOne(`#${element.id}`);
      if (node) {
        const edges = getNodeSnappingEdges(node);
        vertical.push(...edges.vertical.map(e => ({ ...e, offset: 0 })));
        horizontal.push(...edges.horizontal.map(e => ({ ...e, offset: 0 })));
      }
    });

    return { vertical, horizontal };
  }, [stage, elements, getNodeSnappingEdges, templateWidth, templateHeight]);

  /**
   * Get active resize edges based on transformer state
   */
  const getActiveResizeEdges = useCallback((node: Konva.Node) => {
    const box = node.getClientRect({ relativeTo: node.getParent() || undefined });
    return {
      vertical: [box.x, box.x + box.width / 2, box.x + box.width],
      horizontal: [box.y, box.y + box.height / 2, box.y + box.height]
    };
  }, []);

  /**
   * Handle drag snapping
   */
  const handleDragSnapping = useCallback((node: Konva.Node) => {
    if (!stage || !snapConfig.enabled) {
      return;
    }

    const snapToleranceWorld = snapConfig.threshold / camera.scale;
    const nodeId = node.id();
    const nodeEdges = getNodeSnappingEdges(node);
    const guides = getGuides(nodeId); // Exclude current node from guides
    const visibleLines: SnapLine[] = [];

    let snapX: number | null = null;
    let snapY: number | null = null;
    let minDiffX = snapToleranceWorld;
    let minDiffY = snapToleranceWorld;

    // Find closest vertical snap
    nodeEdges.vertical.forEach(nodeEdge => {
      guides.vertical.forEach(guide => {
        const diff = Math.abs(guide.guide - nodeEdge.guide);
        if (diff < minDiffX) {
          minDiffX = diff;
          snapX = guide.guide;
        }
      });
    });

    // Find closest horizontal snap
    nodeEdges.horizontal.forEach(nodeEdge => {
      guides.horizontal.forEach(guide => {
        const diff = Math.abs(guide.guide - nodeEdge.guide);
        if (diff < minDiffY) {
          minDiffY = diff;
          snapY = guide.guide;
        }
      });
    });

    // Apply snapping and create snap lines
    if (snapX !== null) {
      const currentNodeEdges = getNodeSnappingEdges(node);

      // Find which edge should snap to the guide
      let targetEdge = currentNodeEdges.vertical[0]; // default to left edge
      let minEdgeDiff = Math.abs(snapX - targetEdge.guide);

      currentNodeEdges.vertical.forEach(edge => {
        const diff = Math.abs(snapX! - edge.guide);
        if (diff < minEdgeDiff) {
          minEdgeDiff = diff;
          targetEdge = edge;
        }
      });

      // Calculate the offset needed
      const offsetX = snapX - targetEdge.guide;
      node.x(node.x() + offsetX);

      visibleLines.push({
        points: [snapX, -9999, snapX, 9999] // Draw long lines in world space
      });
    }

    if (snapY !== null) {
      const currentNodeEdges = getNodeSnappingEdges(node);

      // Find which edge should snap to the guide
      let targetEdge = currentNodeEdges.horizontal[0]; // default to top edge
      let minEdgeDiff = Math.abs(snapY - targetEdge.guide);

      currentNodeEdges.horizontal.forEach(edge => {
        const diff = Math.abs(snapY! - edge.guide);
        if (diff < minEdgeDiff) {
          minEdgeDiff = diff;
          targetEdge = edge;
        }
      });

      // Calculate the offset needed
      const offsetY = snapY - targetEdge.guide;
      node.y(node.y() + offsetY);

      visibleLines.push({
        points: [-9999, snapY, 9999, snapY] // Draw long lines in world space
      });
    }

    setSnapLines(visibleLines);
  }, [stage, snapConfig, getGuides, getNodeSnappingEdges, camera.scale]);

  /**
   * Handle resize snapping
   */
  const handleResizeSnapping = useCallback((node: Konva.Node) => {
    if (!stage || !resizeSnapConfig.enabled) return;

    const resizeToleranceWorld = resizeSnapConfig.threshold / camera.scale;
    const nodeId = node.id();
    const activeEdges = getActiveResizeEdges(node);
    const guides = getGuides(nodeId);
    const visibleLines: SnapLine[] = [];

    // Find and apply snapping for active edges
    activeEdges.vertical.forEach(edge => {
      guides.vertical.forEach(guide => {
        const diff = Math.abs(guide.guide - edge);
        if (diff < resizeToleranceWorld) {
          visibleLines.push({
            points: [guide.guide, -9999, guide.guide, 9999]
          });
        }
      });
    });

    activeEdges.horizontal.forEach(edge => {
      guides.horizontal.forEach(guide => {
        const diff = Math.abs(guide.guide - edge);
        if (diff < resizeToleranceWorld) {
          visibleLines.push({
            points: [-9999, guide.guide, 9999, guide.guide]
          });
        }
      });
    });

    setSnapLines(visibleLines);
  }, [stage, resizeSnapConfig, getGuides, getActiveResizeEdges, camera.scale]);

  /**
   * Clear snap lines
   */
  const clearSnapLines = useCallback(() => {
    setSnapLines([]);
  }, []);

  return {
    snapLines,
    updateStage,
    handleDragSnapping,
    handleResizeSnapping,
    clearSnapLines,
    snapConfig,
    resizeSnapConfig
  };
};